import React, { useState, useEffect } from 'react';
import { projectService, Project } from '../services/projectService';
import ProjectDetailsModal from '../components/projects/ProjectDetailsModal';
import '../styles/dashboard-pages.css';

const ProjectsPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [projectData, setProjectData] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    setLoading(true);
    setError(null);
    try {
      const projectList = await projectService.listProjects();
      setProjects(projectList);
    } catch (err: any) {
      setError(err.message || 'Failed to load projects');
      console.error('Error loading projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleProjectClick = async (projectId: string) => {
    try {
      setLoading(true);
      const data = await projectService.getProject(projectId);
      setProjectData(data);
      setSelectedProjectId(projectId);
      setIsModalOpen(true);
    } catch (err: any) {
      alert(`Failed to load project details: ${err.message || 'Unknown error'}`);
      console.error('Error loading project:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProjectId(null);
    setProjectData(null);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const filteredProjects = projects.filter((project) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      project.name.toLowerCase().includes(query) ||
      (project.display_name && project.display_name.toLowerCase().includes(query)) ||
      (project.description && project.description.toLowerCase().includes(query))
    );
  });

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <h1>Projects</h1>
        <p>Manage and organize your machine learning projects</p>
      </div>

      <div className="page-actions">
        <button className="action-btn primary">
          + New Project
        </button>
        <button className="action-btn secondary">
          📁 Import Project
        </button>
      </div>

      <div className="page-sections">
        <section className="section-card">
          <div className="section-header">
            <h2>Your Projects</h2>
            <input
              type="text"
              placeholder="Search projects..."
              className="search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="empty-state">
              <div className="empty-icon">⏳</div>
              <p>Loading projects...</p>
            </div>
          ) : error ? (
            <div className="empty-state">
              <div className="empty-icon">⚠️</div>
              <p>Error: {error}</p>
              <button className="action-btn secondary" onClick={loadProjects} style={{ marginTop: '12px' }}>
                Retry
              </button>
            </div>
          ) : filteredProjects.length > 0 ? (
            <div className="projects-list">
              {filteredProjects.map((project) => (
                <div 
                  key={project.id} 
                  className="project-item"
                  onClick={() => handleProjectClick(project.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="project-info">
                    <div className="project-name">{project.display_name || project.name}</div>
                    <div className="project-description">{project.description || 'No description'}</div>
                    <div className="project-meta">Created: {formatDate(project.created_at)}</div>
                  </div>
                  <div className="project-status">
                    <span className={`status-badge ${(project.status || 'active').toLowerCase()}`}>
                      {project.status || 'active'}
                    </span>
                  </div>
                  <div className="project-actions">
                    <button 
                      className="icon-btn" 
                      title="Open"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleProjectClick(project.id);
                      }}
                    >
                      📂
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">📦</div>
              <p>
                {searchQuery 
                  ? 'No projects match your search.' 
                  : 'No projects yet. Create your first project to get started.'}
              </p>
            </div>
          )}
        </section>

        <section className="section-card">
          <h2>Quick Actions</h2>
          <div className="quick-actions-grid">
            <div className="quick-action-card">
              <div className="qa-icon">📊</div>
              <div className="qa-label">View All Experiments</div>
            </div>
            <div className="quick-action-card">
              <div className="qa-icon">📈</div>
              <div className="qa-label">Compare Models</div>
            </div>
            <div className="quick-action-card">
              <div className="qa-icon">📥</div>
              <div className="qa-label">Import Dataset</div>
            </div>
            <div className="quick-action-card">
              <div className="qa-icon">🤝</div>
              <div className="qa-label">Share Project</div>
            </div>
          </div>
        </section>

        <section className="section-card">
          <h2>Project Templates</h2>
          <div className="template-grid">
            <div className="template-card">
              <div className="template-icon">🖼️</div>
              <div className="template-name">Computer Vision</div>
              <div className="template-desc">Image analysis & detection</div>
            </div>
            <div className="template-card">
              <div className="template-icon">📊</div>
              <div className="template-name">Data Analytics</div>
              <div className="template-desc">Data exploration & insights</div>
            </div>
            <div className="template-card">
              <div className="template-icon">💬</div>
              <div className="template-name">NLP</div>
              <div className="template-desc">Text processing & analysis</div>
            </div>
            <div className="template-card">
              <div className="template-icon">🎵</div>
              <div className="template-name">Audio</div>
              <div className="template-desc">Sound & speech recognition</div>
            </div>
          </div>
        </section>
      </div>

      {isModalOpen && projectData && (
        <ProjectDetailsModal
          projectData={projectData}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default ProjectsPage;
